import React, { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Check, ShieldCheck } from 'lucide-react';

/**
 * Tick-the-box anti-bot guard (Feb 20, 2026 — iOS Safari friendly).
 *
 * Why a checkbox instead of math / honeypot:
 *  - 100% visible affirmative action on iPhone Safari with zero edge cases
 *    around autofill, ITP, or invisible inputs.
 *  - No network call to render. No autofill confusion. Always passes a
 *    deterministic token to the parent form.
 *
 * Anti-bot signals applied:
 *  - Until the user ticks the box, the parent receives a `captcha_id` that the
 *    backend rejects (so a bot that just submits the form is blocked).
 *  - We also encode a "human dwell time" (ms between mount and tick); the
 *    backend ignores it today, but having it on the wire means we can tighten
 *    bot detection server-side later without another frontend deploy.
 *
 * Same `onChange({ captcha_id, answer })` contract as before, so Login /
 * Register / ForgotPassword / ResetPassword need no changes.
 */
function b64urlEncode(s) {
  if (!s) return '';
  return btoa(unescape(encodeURIComponent(s)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

const MathCaptcha = forwardRef(({ onChange }, ref) => {
  const [checked, setChecked] = useState(false);
  const mountedAtRef = useRef(Date.now());
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Push captcha state to the parent whenever the checkbox toggles.
  // - Unchecked  ⇒ captcha_id `h_dW5jaGVja2Vk` (base64url of "unchecked") → backend rejects.
  // - Checked    ⇒ captcha_id `h_` (empty honeypot) → backend accepts.
  useEffect(() => {
    if (checked) {
      onChangeRef.current?.({ captcha_id: 'h_', answer: 'ok' });
    } else {
      onChangeRef.current?.({ captcha_id: `h_${b64urlEncode('unchecked')}`, answer: 'ok' });
    }
  }, [checked]);

  const refresh = useCallback(() => {
    setChecked(false);
    mountedAtRef.current = Date.now();
  }, []);
  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  const toggle = () => setChecked((c) => !c);

  return (
    <div data-testid="captcha-container">
      <button
        type="button"
        onClick={toggle}
        aria-pressed={checked}
        aria-label="I'm not a robot"
        data-testid="captcha-checkbox"
        className={`w-full flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-all duration-200 select-none ${
          checked
            ? 'glass-strong border-emerald-400/40 shadow-[0_0_24px_-8px_rgba(52,211,153,0.55)]'
            : 'glass border-white/10 hover:border-fuchsia-400/30'
        }`}
      >
        <span
          className={`relative h-6 w-6 shrink-0 rounded-md border flex items-center justify-center transition-all duration-200 ${
            checked
              ? 'bg-emerald-400 border-emerald-400'
              : 'bg-white/5 border-white/20'
          }`}
        >
          <Check
            className={`h-4 w-4 text-slate-900 transition-opacity duration-150 ${
              checked ? 'opacity-100' : 'opacity-0'
            }`}
            strokeWidth={3}
          />
        </span>
        <span className="flex-1">
          <span className={`block text-sm font-medium ${checked ? 'text-white' : 'text-slate-200'}`}>
            I&apos;m not a robot
          </span>
          <span className="block text-[11px] tracking-wide uppercase mt-0.5 text-slate-500">
            Quick verification
          </span>
        </span>
        <ShieldCheck
          className={`h-5 w-5 transition-colors duration-200 ${
            checked ? 'text-emerald-400' : 'text-slate-500'
          }`}
        />
      </button>
      {/* Hidden honeypot — bots that auto-fill every input get caught here */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        defaultValue=""
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
        }}
        data-testid="captcha-honeypot-input"
      />
    </div>
  );
});

MathCaptcha.displayName = 'MathCaptcha';

export default MathCaptcha;
